/**
 * VH-253: the paste box — the main way the list gets filled.
 *
 * A textarea and one button. The parser lives on the server, so this component's only
 * real job is what happens afterwards:
 *
 *  - something was recognised → say how many were added and merged, and CLEAR the box
 *  - `nothing_recognised` (422) → say nothing was found and KEEP the text, so it can
 *    be edited and retried rather than retyped
 *  - anything else → say the import failed, and keep the text for the same reason
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function PasteImport({ onImport, pending = false, defaultOpen = false }) {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [open, setOpen] = useState(defaultOpen)
  const [feedback, setFeedback] = useState(null)

  // `defaultOpen` arrives late: the list is still loading on first render, so the
  // screen cannot know it is empty yet. Open the box the first time it turns true and
  // never again — after that, opening and closing belongs to the user.
  const autoOpened = useRef(false)
  useEffect(() => {
    if (defaultOpen && !autoOpened.current) {
      autoOpened.current = true
      setOpen(true)
    }
  }, [defaultOpen])

  const submit = async (event) => {
    event.preventDefault()
    const trimmed = text.trim()
    if (!trimmed) return
    setFeedback(null)
    const outcome = await onImport(trimmed)

    if (outcome?.status === 'ok') {
      setText('')
      setFeedback({
        tone: 'ok',
        message: t('shopping.paste.imported', {
          added: outcome.added_count ?? 0,
          merged: outcome.merged_count ?? 0,
        }),
      })
      return
    }
    setFeedback({
      tone: 'warn',
      message:
        outcome?.status === 'nothing'
          ? t('shopping.paste.nothingRecognised')
          : t('shopping.paste.failed'),
    })
  }

  return (
    <div className="rounded-lg border border-outline-variant bg-surface-container">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-3 py-3 min-h-[44px] text-left text-sm font-medium text-on-surface"
        data-testid="shopping-paste-toggle"
      >
        {t('shopping.paste.title')}
        <span aria-hidden="true" className="text-on-surface-variant">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <form onSubmit={submit} className="px-3 pb-3" data-testid="shopping-paste-form">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            maxLength={50000}
            placeholder={t('shopping.paste.placeholder')}
            aria-label={t('shopping.paste.aria')}
            className="input w-full"
            data-testid="shopping-paste-text"
          />
          <button
            type="submit"
            className="btn-primary mt-2 w-full min-h-[44px]"
            disabled={pending || !text.trim()}
            data-testid="shopping-paste-submit"
          >
            {pending ? t('shopping.paste.importing') : t('shopping.paste.import')}
          </button>
        </form>
      )}

      {feedback && (
        <p
          role="status"
          className={`px-3 pb-3 text-sm ${
            feedback.tone === 'ok' ? 'text-success-700' : 'text-warning-700'
          }`}
          data-testid="shopping-paste-feedback"
        >
          {feedback.message}
        </p>
      )}
    </div>
  )
}
